import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { BookOpen, FileDown, ChevronRight, Lightbulb } from "lucide-react";

const HelpPage = () => {
  const [manual, setManual] = useState(null);
  const [active, setActive] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { api.get("/manual").then((r) => setManual(r.data)).catch(() => toast.error("Manuel indisponible")); }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/manual.pdf", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = "manuel_utilisation.pdf"; a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Erreur lors du téléchargement"); }
    finally { setDownloading(false); }
  };

  if (!manual) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  const ch = manual.chapters[active];

  return (
    <div className="space-y-6" data-testid="help-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight text-slate-900 flex items-center gap-2"><BookOpen className="w-6 h-6 text-green-700" /> Manuel d'utilisation</h1>
          <p className="text-slate-500 mt-1 text-sm">{manual.title}</p>
        </div>
        <Button onClick={downloadPdf} disabled={downloading} className="btn-primary" data-testid="download-manual-btn">{downloading ? <div className="spinner w-4 h-4" /> : <><FileDown className="w-4 h-4 mr-2" /> Télécharger le PDF</>}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <nav className="lg:col-span-4 space-y-1" data-testid="manual-toc">
          {manual.chapters.map((c, i) => (
            <button key={i} onClick={() => setActive(i)} className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center justify-between transition-colors ${i === active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`manual-chapter-${i}`}>
              <span><span className="font-mono text-xs opacity-60 mr-2">{String(i + 1).padStart(2, "0")}</span>{c.title}</span>
              <ChevronRight className="w-4 h-4 opacity-60" />
            </button>
          ))}
        </nav>
        <Card className="lg:col-span-8">
          <CardContent className="p-6 space-y-5" data-testid="manual-content">
            <h2 className="text-lg font-bold font-[Chivo] text-slate-900">{active + 1}. {ch.title}</h2>
            {ch.intro && <p className="text-sm text-slate-600">{ch.intro}</p>}
            {ch.sections.map((s, i) => (
              <div key={i}>
                <h3 className="font-semibold text-slate-900 text-sm mb-2 border-l-4 border-green-600 pl-2">{s.title}</h3>
                <ol className="space-y-1.5 text-sm text-slate-700 list-decimal pl-5">{s.steps.map((st, j) => <li key={j}>{st}</li>)}</ol>
                {s.tip && <p className="mt-2 text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded-sm px-3 py-2 flex items-start gap-2"><Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {s.tip}</p>}
              </div>
            ))}
            <div className="flex justify-between pt-4 border-t border-slate-100">
              <Button variant="outline" size="sm" disabled={active === 0} onClick={() => setActive(active - 1)} data-testid="manual-prev-btn">Chapitre précédent</Button>
              <Button variant="outline" size="sm" disabled={active === manual.chapters.length - 1} onClick={() => setActive(active + 1)} data-testid="manual-next-btn">Chapitre suivant</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpPage;
